import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Field, FieldGroup, Fieldset, Label } from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import { bankService } from './clients/ApiClients';
import { CheckReturn } from './clients/rust-api-client';
import { formatCurrency, getBankErrorParts } from './utils/bankHelpers';

interface VoidCheckDialogProps {
  /** The check being voided; `null` closes the dialog. */
  check: CheckReturn | null;
  hoaId: string;
  onClose: () => void;
  onVoided: (after: CheckReturn) => void;
}

/**
 * Void a recorded check. Reverses the journal entry and, when the bank
 * accepted the issue, registers the void with the bank as well. A reason
 * is required: the void is a second entry at the bank and a reversal in
 * the books, and both outlive whoever remembers why.
 */
const VoidCheckDialog: React.FC<VoidCheckDialogProps> = ({
  check,
  hoaId,
  onClose,
  onVoided,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (check) {
      setReason('');
      setError(null);
    }
  }, [check]);

  const handleVoid = async () => {
    if (!check) return;
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const resp = await bankService.voidHoaCheck({
        hoaId,
        id: check.id,
        checkVoidRequest: { reason: reason.trim() },
      });
      onVoided(resp.data);
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setError(body || "Couldn't void the check.");
    } finally {
      setSubmitting(false);
    }
  };

  const atBank =
    check?.pp_status === 'sent' || check?.pp_status === 'downloaded';

  return (
    <Dialog open={!!check} onClose={submitting ? () => undefined : onClose}>
      <DialogTitle>Void check #{check?.check_number}</DialogTitle>
      <DialogDescription>
        {check
          ? `${formatCurrency(Number(check.amount))} to ${check.payee_name}. `
          : ''}
        {atBank
          ? 'The journal entry is reversed and the bank is told the check is void.'
          : 'The journal entry is reversed. The bank had not accepted this check, so nothing is sent.'}
      </DialogDescription>
      <DialogBody>
        {error && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle="Cannot void"
              alertMessage={error}
              isVisible={!!error}
              onClose={() => setError(null)}
              timeout={12000}
              isInline={true}
            />
          </div>
        )}
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Reason</Label>
              <Input
                name="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. printed on the wrong stock"
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <Text className="mt-3 text-xs">
          A void cannot be undone. Record a new check if one is still owed.
        </Text>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button color="red" onClick={handleVoid} disabled={submitting}>
          {submitting ? 'Voiding…' : 'Void check'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidCheckDialog;
